import { Drawer, Checkbox } from 'antd'
import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { Edit, Undo } from '@lmc/ui-kit'
import { DrawerBody } from './components'
import { Spinner } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import useUser from 'hooks/useUser/useUser'
import { useState } from 'react'
import { getLastChangedBy } from '../utils'
import { DrawerConfirmAction } from './components'
import { capitalize } from 'lodash'
import useRevisionPublish from '../hooks/useRevisionPublish'
import useRevisionRollback from '../hooks/useRevisionRollback'
import useRevisionReopen from '../hooks/useRevisionReopen'
import { useShiftTypesList } from '../hooks/useShiftTypesList'
import { useVisitTypesList } from '../hooks/useVisitTypesList'
import { SHIFT, VISIT } from '../constants'
import useRevisionDetail from '../hooks/useRevisionDetail'
import useFeatureFlags from 'hooks/useFeatureFlags'

type RotaDrawerProps = {
  isVisible: boolean
  onClose: () => void
  id: string
  defaultTab?: number
  publishedRota?: boolean
}

export const RotaDrawer = ({
  isVisible,
  onClose,
  defaultTab = 1,
  id,
  publishedRota
}: RotaDrawerProps) => {
  const { t } = useTranslation()

  const flags = useFeatureFlags({
    flagNames: ['rota_published_email']
  })?.data
  const flagsRotaPublishedEmail = flags?.rota_published_email?.enabled

  const [sendEmail, setSendEmail] = useState(false)

  const { revisionDetail, isLoadingRevisionDetail } = useRevisionDetail({
    id
  })

  const { rollbackRevision, isRollbackRevisionLoading } = useRevisionRollback({
    id: revisionDetail?.id
  })

  const { publishRevision, isPublishRevisionLoading } = useRevisionPublish({
    id: revisionDetail?.id
  })

  const { reopenRevision, isReopenRevisionLoading } = useRevisionReopen({
    id: revisionDetail?.id
  })

  const { visitTypes, isVisitTypesLoading } = useVisitTypesList()

  const { shiftTypes, isShiftTypesLoading } = useShiftTypesList()

  const { user: publishedByUser, isLoading: isPublishUserLoading } = useUser({
    id: revisionDetail?.updatedBy?.id
  })

  const [tab, setTab] = useState('1')

  const handleTabChange = (activeKey: string) => setTab(activeKey)

  const handlePublish = () => {
    publishRevision?.({ sendEmail })
  }

  const handleUndoChanges = () => {
    rollbackRevision?.()
  }

  const handleReopen = () => {
    reopenRevision?.()
  }

  const showCta = tab === '1' || tab === '2'

  const typeOptions = revisionDetail?.siteShift ? shiftTypes : visitTypes

  const typeOfShiftVisit = typeOptions?.find(
    (item) => item?.id === revisionDetail?.instanceTypeId
  )?.name

  const isLoading =
    isVisitTypesLoading ||
    isShiftTypesLoading ||
    isPublishUserLoading ||
    isLoadingRevisionDetail

  const isDraft = revisionDetail?.status === 'draft'

  const isCancelled = revisionDetail?.status === 'cancelled'

  const isModified = revisionDetail?.modified

  const isPublishedAndNotModified = !isDraft && !isCancelled && !isModified

  const type = revisionDetail?.siteShift ? SHIFT : VISIT

  return (
    <StyledDrawer
      title={
        <>
          {isLoading && (
            <LoadingWrapper>
              <Spinner />
            </LoadingWrapper>
          )}
          {!isLoading && (
            <TitleWrapper>
              <StyledTitleText>
                {capitalize(type)}
                {typeOfShiftVisit ? ` / ${typeOfShiftVisit}` : ''}
              </StyledTitleText>
              <StyledTitleHeading>{revisionDetail?.title}</StyledTitleHeading>
            </TitleWrapper>
          )}
        </>
      }
      placement="right"
      onClose={onClose}
      open={isVisible}
      width={736}
      footer={
        !publishedRota &&
        !isPublishedAndNotModified && (
          <>
            {isLoading && showCta && (
              <LoadingWrapper>
                <Spinner />
              </LoadingWrapper>
            )}
            {!isLoading && showCta && (
              <FooterWrapper>
                <FooterSectionWrapper>
                  <StyledFooterTitleText level={2}>
                    {isCancelled ? t('CANCELED_BY') : t('LAST_CHANGED_BY')}
                  </StyledFooterTitleText>
                  <StyledFooterTitleHeading>
                    {getLastChangedBy(publishedByUser, revisionDetail)}
                  </StyledFooterTitleHeading>
                </FooterSectionWrapper>
                <FooterSectionButtonWrapper>
                  {isModified && <UpdateIndicator />}
                  {isCancelled && (
                    <DrawerConfirmAction
                      buttonProps={{
                        title: t('REOPEN_EVENT'),
                        icon: <Undo />
                      }}
                      actionProps={{
                        title: t('ARE_REOPEN_EVENT_TITLE', {
                          variable: type
                        }),
                        content: t('ARE_REOPEN_EVENT_DESC'),
                        confirmTitle: t('REOPEN_EVENT')
                      }}
                      onConfirm={handleReopen}
                      isLoading={isReopenRevisionLoading}
                    />
                  )}
                  {isModified && !isCancelled && (
                    <DrawerConfirmAction
                      buttonProps={{
                        title: t('UNDO_CHANGES'),
                        icon: <Undo />
                      }}
                      actionProps={{
                        title: t('ARE_UNDO_CHANGES_TITLE'),
                        content: t('ARE_UNDO_CHANGES_DESC'),
                        confirmTitle: t('UNDO_CHANGES')
                      }}
                      onConfirm={handleUndoChanges}
                      isLoading={isRollbackRevisionLoading}
                    />
                  )}

                  {isDraft && (
                    <DrawerConfirmAction
                      buttonProps={{
                        title: t('PUBLISH'),
                        icon: <Edit />,
                        variant: 'primary'
                      }}
                      actionProps={{
                        title: t('ARE_DRAFT_TITLE'),
                        content: t('ARE_DRAFT_DESC'),
                        confirmTitle: t('PUBLISH')
                      }}
                      onConfirm={handlePublish}
                      isLoading={isPublishRevisionLoading}
                    >
                      {flagsRotaPublishedEmail && (
                        <Checkbox
                          checked={sendEmail}
                          onChange={(e) => setSendEmail(e.target.checked)}
                        >
                          <Text>{t('SEND_EMAIL_NOTIFICATIONS')}</Text>
                        </Checkbox>
                      )}
                    </DrawerConfirmAction>
                  )}
                  {!isDraft && isModified && (
                    <DrawerConfirmAction
                      buttonProps={{
                        title: t('PUBLISH_CHANGES'),
                        icon: <Edit />,
                        variant: 'primary'
                      }}
                      actionProps={{
                        title: t('ARE_PUBLISH_CHANGES_TITLE'),
                        content: t('ARE_PUBLISH_CHANGES_DESC'),
                        confirmTitle: t('PUBLISH_CHANGES')
                      }}
                      onConfirm={handlePublish}
                      isLoading={isPublishRevisionLoading}
                    >
                      {flagsRotaPublishedEmail && (
                        <Checkbox
                          checked={sendEmail}
                          onChange={(e) => setSendEmail(e.target.checked)}
                        >
                          <Text>{t('SEND_EMAIL_NOTIFICATIONS')}</Text>
                        </Checkbox>
                      )}
                    </DrawerConfirmAction>
                  )}
                </FooterSectionButtonWrapper>
              </FooterWrapper>
            )}
          </>
        )
      }
    >
      {isLoading && (
        <LoadingWrapper>
          <Spinner />
        </LoadingWrapper>
      )}
      {!isLoading && (
        <DrawerBody
          revisionDetail={revisionDetail}
          onTabChange={handleTabChange}
          defaultTab={defaultTab}
          publishedRota={publishedRota}
        />
      )}
    </StyledDrawer>
  )
}

const StyledDrawer = styled(Drawer)`
  .ant-drawer-header {
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
  }

  .ant-drawer-header-title {
    flex-direction: row-reverse;
  }

  .ant-drawer-footer {
    padding: 24px;
    box-shadow: 0px -2px 4px 0px rgba(0, 0, 0, 0.1);
  }
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const FooterWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
`

const StyledTitleText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const StyledTitleHeading = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color3};
  font-size: 20px;
  font-weight: 700;
`

const StyledFooterTitleText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const StyledFooterTitleHeading = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color3};
  font-size: 16px;
  font-weight: 400;
`

const FooterSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const FooterSectionButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`
const UpdateIndicator = styled.div`
  position: absolute;
  left: -24.5px;
  top: -24.5px;
  height: 16px;
  width: 16px;
  border-radius: 0px 0px 8px 0px;
  background: ${(props) => props.theme.color.status.popout};
  border: 0.5px solid ${(props) => props.theme.color.neutral.color9};
`
